import { ArrowRight, CircleDot, TreeDeciduous, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";

const ServicesSection = () => {
  const services = [
    {
      id: "stump-grinding",
      icon: CircleDot,
      title: "Stump Grinding",
      description:
        "Professional stump grinding services using state-of-the-art equipment. We grind stumps 6-12 inches below ground level, leaving your yard ready for landscaping, new grass, or garden beds.",
      features: [
        "Same-day service available",
        "All stump sizes handled",
        "Clean debris removal",
        "Free mulch from grinding",
      ],
      image: "https://images.pexels.com/photos/209229/pexels-photo-209229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      id: "stump-removal",
      icon: TreeDeciduous,
      title: "Tree Stump Removal",
      description:
        "Complete tree stump removal for new construction, landscaping projects, or property improvement. We remove the entire root ball when required for foundations, pools, or new plantings.",
      features: [
        "Full root system removal",
        "Soil backfill included",
        "Site restoration",
        "Heavy equipment available",
      ],
      image: "https://images.pexels.com/photos/4403500/pexels-photo-4403500.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
      id: "root-grinding",
      icon: Scissors,
      title: "Root Grinding",
      description:
        "Surface root grinding eliminates trip hazards and restores lawn aesthetics. Perfect for roots damaging sidewalks, driveways, patios, or foundations without removing the entire tree.",
      features: [
        "Surface root removal",
        "Prevents future regrowth",
        "Lawn restoration ready",
        "Minimal yard disruption",
      ],
      image: "https://images.pexels.com/photos/901941/pexels-photo-901941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
  ];

  return (
    <section id="services" className="py-16 lg:py-24 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-left mb-12 lg:mb-16">
          <div className="accent-line mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] uppercase mb-4">
            Stump Grinding &amp; Removal Services
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl">
            Professional stump grinding and tree stump removal services near you. Licensed and insured local stump grinding for residential and commercial properties of all sizes.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`industrial-card overflow-hidden ${
                index === 0 ? "md:col-span-2 lg:col-span-1" : ""
              }`}
              data-testid={`service-card-${service.id}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent opacity-60" />
                <div className="absolute bottom-4 left-4">
                  <div className="service-icon rounded-sm">
                    <service.icon className="w-6 h-6" strokeWidth={2.5} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#1A1A1A] uppercase mb-3">
                  {service.title}
                </h3>
                <p className="font-body text-gray-600 mb-4 text-sm leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#FF4D00]" />
                      <span className="font-body text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild
                  className="w-full btn-secondary py-3 rounded-none flex items-center justify-center gap-2"
                >
                  <a href="#quote">
                    Get Quote
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-12 lg:mt-16 bg-[#1A1A1A] p-8 lg:p-12 relative overflow-hidden noise-bg">
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white uppercase mb-2">
                Ready to Remove That Stump?
              </h3>
              <p className="font-body text-gray-400 text-lg">
                Get a free, no-obligation quote today. Most jobs completed same day!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="btn-primary px-8 py-4 text-lg rounded-none"
                data-testid="services-cta-quote"
              >
                <a href="#quote">Get Free Quote</a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] px-8 py-4 text-lg rounded-none font-heading font-bold uppercase tracking-wide"
                data-testid="services-cta-call"
              >
                <a href="tel:+15202007739,2">(520) 200-7739 Ext 2</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
